import { Transaction } from '../types/transaction';

/**
 * Statistics for a specific day's spending
 */
export interface DayStats {
  date: Date;
  amount: number;
  isWeekend: boolean;
  dayName: string;
}

/**
 * Analyzes transactions to find the day with the highest spending
 * @param transactions - Array of transactions to analyze
 * @returns DayStats object containing date, amount, and day information for the most expensive day
 */
export const getMostExpensiveDay = (transactions: Transaction[]): DayStats | null => {
  if (!transactions?.length) {
    return null;
  }

  // Group transactions by day and calculate daily totals
  const dailyTotals = new Map<string, number>();
  const dateMap = new Map<string, Date>();

  transactions.forEach(transaction => {
    // Only consider spending (negative amounts)
    if (transaction.amount < 0) {
      const date = new Date(transaction.date);
      const dateKey = date.toISOString().split('T')[0];
      const amount = Math.abs(transaction.amount);
      
      dailyTotals.set(dateKey, (dailyTotals.get(dateKey) || 0) + amount);
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, date);
      }
    }
  });

  if (dailyTotals.size === 0) {
    return null;
  }

  // Find the day with maximum spending
  let maxAmount = 0;
  let maxDate: Date = new Date();

  dailyTotals.forEach((amount, dateKey) => {
    if (amount > maxAmount) {
      maxAmount = amount;
      maxDate = dateMap.get(dateKey)!;
    }
  });

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = dayNames[maxDate.getDay()];
  const isWeekend = maxDate.getDay() === 0 || maxDate.getDay() === 6;

  return {
    date: maxDate,
    amount: maxAmount,
    isWeekend,
    dayName
  };
};
